import { createBrowserRouter } from 'react-router-dom';

import { ROUTE_PATHS } from '@dmp/constant';
import { AppLayout } from '@dmp/layouts/AppLayout/AppLayout';
import { AuthLayout } from '@dmp/layouts/AuthLayout/AuthLayout';
import { InternalAppLayout } from '@dmp/layouts/InternalAppLayout/InternalAppLayout';
import { PolicyLayout } from '@dmp/layouts/PolicyLayout/PolicyLayout';
import { LandingPage } from '@dmp/pages/LandingPage/LandingPage';

import { RootLayout } from './RootLayout';
import { AgencyCatalogAdminGuard, AgencyCatalogGuard } from './RouteGuard/AgencyCatalogGuard';
import { InternalAppsGuard } from './RouteGuard/InternalAppsGuard';
import { LoginGuard } from './RouteGuard/LoginGuard';
import { RouteGuard } from './RouteGuard/RouteGuard';
import { importWithRetry } from './router.utils';

export const router = createBrowserRouter([
  {
    // App-wide root: fires a GTM page_view on every navigation across all route groups.
    Component: RootLayout,
    children: [
      {
        Component: RouteGuard,
        children: [
          {
            path: ROUTE_PATHS.LANDING_PAGE,
            Component: LandingPage,
          },
          {
            path: ROUTE_PATHS.MASTER,
            lazy: {
              Component: importWithRetry(() => import('@dmp/pages/Master/Master')),
            },
          },
          {
            Component: AppLayout,
            children: [
              {
                path: ROUTE_PATHS.DATA_CATALOG,
                lazy: {
                  Component: importWithRetry(() => import('@dmp/pages/DataCatalog/DataCatalog')),
                },
              },
              {
                path: ROUTE_PATHS.REQUEST_NEW_DATASET,
                lazy: {
                  Component: importWithRetry(() => import('@dmp/pages/RequestNewDataset/RequestNewDataset')),
                },
              },
              {
                path: ROUTE_PATHS.CONTACT_SALES_TEAM,
                lazy: {
                  Component: importWithRetry(() => import('@dmp/pages/ContactSalesTeam/ContactSalesTeam')),
                },
              },
              {
                path: ROUTE_PATHS.PARTNERS,
                lazy: {
                  Component: importWithRetry(() => import('@dmp/pages/Partner/Partners/Partners')),
                },
              },
              {
                path: ROUTE_PATHS.BECOME_A_PARTNER,
                lazy: {
                  Component: importWithRetry(() => import('@dmp/pages/Partner/BecomeAPartner/BecomeAPartner')),
                },
              },
              {
                path: ROUTE_PATHS.DATASET,
                lazy: {
                  Component: importWithRetry(() => import('@dmp/pages/DatasetDetail/PublicDataset/PublicDataset')),
                },
              },
              {
                path: ROUTE_PATHS.PREVIEW_FILE,
                lazy: {
                  Component: importWithRetry(() => import('@dmp/pages/PreviewFile/PreviewFile')),
                },
              },

              {
                Component: LoginGuard,
                children: [
                  {
                    path: ROUTE_PATHS.REQUEST_AVAILABLE_DATASET,
                    lazy: {
                      Component: importWithRetry(
                        () => import('@dmp/pages/RequestAvailableDataset/RequestAvailableDataset')
                      ),
                    },
                  },
                  {
                    path: ROUTE_PATHS.ADD_OWN_DATASET,
                    lazy: {
                      Component: importWithRetry(() => import('@dmp/pages/DataHub/AddOwnDataset/AddOwnDataset')),
                    },
                  },
                  {
                    path: ROUTE_PATHS.DATA_HUB,
                    lazy: {
                      Component: importWithRetry(() => import('@dmp/pages/DataHub/DataHub')),
                    },
                  },
                  {
                    path: ROUTE_PATHS.DATA_HUB_DATASET,
                    lazy: {
                      Component: importWithRetry(
                        () => import('@dmp/pages/DatasetDetail/PrivateDataset/PrivateDataset')
                      ),
                    },
                  },
                  {
                    path: ROUTE_PATHS.OWN_DATASET,
                    lazy: {
                      Component: importWithRetry(
                        () => import('@dmp/pages/DatasetDetail/PrivateDataset/PrivateDataset')
                      ),
                    },
                  },
                  {
                    path: ROUTE_PATHS.ENHANCE_DATASET,
                    lazy: {
                      Component: importWithRetry(() => import('@dmp/pages/EnhanceDataset/EnhanceDataset')),
                    },
                  },
                  {
                    path: ROUTE_PATHS.VIEW_GUIDELINES,
                    lazy: {
                      Component: importWithRetry(
                        () => import('@dmp/pages/EnhanceDataset/ViewGuidelines/ViewGuidelines')
                      ),
                    },
                  },
                  {
                    path: ROUTE_PATHS.VIEW_QA_INSTRUCTIONS,
                    lazy: {
                      Component: importWithRetry(
                        () => import('@dmp/pages/EnhanceDataset/ViewQaInstructions/ViewQaInstructions')
                      ),
                    },
                  },
                  {
                    path: ROUTE_PATHS.ADD_TO_DATA_HUB,
                    lazy: {
                      Component: importWithRetry(() => import('@dmp/pages/DatasetDetail/AddToDataHub/AddToDataHub')),
                    },
                  },
                  {
                    path: ROUTE_PATHS.PROFILE,
                    lazy: {
                      Component: importWithRetry(() => import('@dmp/pages/Profile/Profile')),
                    },
                  },
                  {
                    path: ROUTE_PATHS.SETTINGS,
                    lazy: {
                      Component: importWithRetry(() => import('@dmp/pages/Settings/Setting')),
                    },
                  },
                  {
                    Component: AgencyCatalogGuard,
                    children: [
                      {
                        path: ROUTE_PATHS.AGENCY_CATALOG,
                        lazy: {
                          Component: importWithRetry(() => import('@dmp/pages/Agency/AgencyCatalog/AgencyCatalog')),
                        },
                      },
                      {
                        path: ROUTE_PATHS.AGENCY_DETAIL,
                        lazy: {
                          Component: importWithRetry(() => import('@dmp/pages/Agency/AgencyDetail/AgencyDetail')),
                        },
                      },
                      {
                        Component: AgencyCatalogAdminGuard,
                        children: [
                          {
                            path: ROUTE_PATHS.EDIT_AGENCY,
                            lazy: {
                              Component: importWithRetry(() => import('@dmp/pages/Agency/EditAgency/EditAgency')),
                            },
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: ROUTE_PATHS.COLLECTION_DETAIL,
                    lazy: {
                      Component: importWithRetry(
                        () => import('@dmp/pages/Collection/CollectionDetail/CollectionDetail')
                      ),
                    },
                  },
                  {
                    path: ROUTE_PATHS.COLLECTION_MANAGEMENT,
                    lazy: {
                      Component: importWithRetry(
                        () => import('@dmp/pages/Collection/CollectionManagement/CollectionManagement')
                      ),
                    },
                  },
                  {
                    path: ROUTE_PATHS.COLLECTION_DATASET_DETAIL,
                    lazy: {
                      Component: importWithRetry(
                        () => import('@dmp/pages/DatasetDetail/PrivateDataset/PrivateDataset')
                      ),
                    },
                  },
                  {
                    path: ROUTE_PATHS.API_TOKENS,
                    lazy: {
                      Component: importWithRetry(() => import('@dmp/pages/ApiTokens/ApiTokens')),
                    },
                  },
                  {
                    path: ROUTE_PATHS.API_TOKENS_USAGE_GUIDE,
                    lazy: {
                      Component: importWithRetry(() => import('@dmp/pages/ApiTokens/UsageGuide/UsageGuide')),
                    },
                  },
                ],
              },
            ],
          },
          {
            Component: InternalAppLayout,
            children: [
              {
                Component: LoginGuard,
                children: [
                  {
                    Component: InternalAppsGuard,
                    loader: () => 'portal',
                    children: [
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_PORTAL,
                        lazy: {
                          Component: importWithRetry(() => import('@dmp/internalAppPages/Portal/Portal')),
                        },
                      },
                    ],
                  },
                  {
                    Component: InternalAppsGuard,
                    loader: () => 'dataset_management',
                    children: [
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_DASHBOARD,
                        lazy: {
                          Component: importWithRetry(
                            () => import('@dmp/internalAppPages/DatasetManagement/Dashboard/Dashboard')
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_BULK_UPLOAD,
                        lazy: {
                          Component: importWithRetry(
                            () => import('@dmp/internalAppPages/DatasetManagement/BulkUpload/BulkUpload')
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_ADD_NEW_DATASET,
                        lazy: {
                          Component: importWithRetry(
                            () => import('@dmp/internalAppPages/DatasetManagement/UpsertDataset/UpsertDataset')
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_ADD_NEW_DATASET_AI,
                        lazy: {
                          Component: importWithRetry(
                            () => import('@dmp/internalAppPages/DatasetManagement/UpsertDatasetAI/UpsertDatasetAI')
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_VIEW_AI_DRAFT,
                        lazy: {
                          Component: importWithRetry(
                            () => import('@dmp/internalAppPages/DatasetManagement/UpsertDatasetAI/UpsertDatasetAI')
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_AI_DRAFTS,
                        lazy: {
                          Component: importWithRetry(
                            () => import('@dmp/internalAppPages/DatasetManagement/AiDrafts/AiDrafts')
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_EDIT_DRAFT_DATASET,
                        lazy: {
                          Component: importWithRetry(
                            () => import('@dmp/internalAppPages/DatasetManagement/UpsertDataset/UpsertDataset')
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_EDIT_DATASET,
                        lazy: {
                          Component: importWithRetry(
                            () => import('@dmp/internalAppPages/DatasetManagement/EditDataset/EditDataset')
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_EDIT_SUBMISSION_UPSERT,
                        lazy: {
                          Component: importWithRetry(
                            () => import('@dmp/internalAppPages/DatasetManagement/UpsertDataset/UpsertDataset')
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_EDIT_SUBMISSION_EDIT_FORM,
                        lazy: {
                          Component: importWithRetry(
                            () => import('@dmp/internalAppPages/DatasetManagement/EditDataset/EditDataset')
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_DATASET_DETAIL,
                        lazy: {
                          Component: importWithRetry(
                            () => import('@dmp/internalAppPages/DatasetManagement/DatasetDetail/DatasetDetail')
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_AGENCY_AND_PROVIDERS,
                        lazy: {
                          Component: importWithRetry(
                            () =>
                              import('@dmp/internalAppPages/DatasetManagement/AgencyAndProviders/AgencyAndProviders')
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_ADD_AGENCY_OR_PROVIDER,
                        lazy: {
                          Component: importWithRetry(
                            () =>
                              import(
                                '@dmp/internalAppPages/DatasetManagement/AgencyAndProviders/AddAgencyOrProvider/AddAgencyOrProvider'
                              )
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_EDIT_AGENCY_OR_PROVIDER,
                        lazy: {
                          Component: importWithRetry(
                            () =>
                              import(
                                '@dmp/internalAppPages/DatasetManagement/AgencyAndProviders/EditAgencyOrProvider/EditAgencyOrProvider'
                              )
                          ),
                        },
                      },
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_MANAGE_META_DATA,
                        lazy: {
                          Component: importWithRetry(
                            () => import('@dmp/internalAppPages/ManageMetaData/ManageMetaData')
                          ),
                        },
                      },
                      {
                        // User Management is admin-only (JWT dataset_management access); Uploaders who
                        // reach dataset_management still get Access Denied here (BE also 403s).
                        Component: InternalAppsGuard,
                        loader: () => 'user_management',
                        children: [
                          {
                            path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_USER_MANAGEMENT,
                            lazy: {
                              Component: importWithRetry(
                                () => import('@dmp/internalAppPages/DatasetManagement/UserManagement/UserManagement')
                              ),
                            },
                          },
                        ],
                      },
                      {
                        // Collection Requests admin tab (DMP-2370) is admin-only, same gate as User Management.
                        Component: InternalAppsGuard,
                        loader: () => 'collection_requests',
                        children: [
                          {
                            path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_COLLECTION_REQUESTS,
                            lazy: {
                              Component: importWithRetry(
                                () =>
                                  import(
                                    '@dmp/internalAppPages/DatasetManagement/CollectionRequests/CollectionRequests'
                                  )
                              ),
                            },
                          },
                        ],
                      },
                      {
                        // Approval Queue (DMP-2386) is admin-only — approving/rejecting Uploader submissions.
                        Component: InternalAppsGuard,
                        loader: () => 'approval_queue',
                        children: [
                          {
                            path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_APPROVAL_QUEUE,
                            lazy: {
                              Component: importWithRetry(
                                () => import('@dmp/internalAppPages/DatasetManagement/ApprovalQueue/ApprovalQueue')
                              ),
                            },
                          },
                        ],
                      },
                      {
                        // My Submissions (DMP-2386) is uploader-only — their own review rounds.
                        Component: InternalAppsGuard,
                        loader: () => 'my_submissions',
                        children: [
                          {
                            path: ROUTE_PATHS.INTERNAL_APP_DATASET_MANAGEMENT_MY_SUBMISSIONS,
                            lazy: {
                              Component: importWithRetry(
                                () => import('@dmp/internalAppPages/DatasetManagement/MySubmissions/MySubmissions')
                              ),
                            },
                          },
                        ],
                      },
                    ],
                  },
                  {
                    Component: InternalAppsGuard,
                    loader: () => 'telemetry',
                    children: [
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_TELEMETRY,
                        lazy: {
                          Component: importWithRetry(() => import('@dmp/internalAppPages/Telemetry/Telemetry')),
                        },
                      },
                    ],
                  },
                  {
                    // RBAC is exclusive to the RBAC Administrator, and only rendered when the
                    // unified RBAC layer is enabled for this tenant.
                    Component: InternalAppsGuard,
                    loader: () => 'rbac',
                    children: [
                      {
                        path: ROUTE_PATHS.INTERNAL_APP_RBAC,
                        lazy: {
                          Component: importWithRetry(() => import('@dmp/internalAppPages/Rbac/Rbac')),
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        // Deliberately outside RouteGuard: that guard hard-redirects an unauthenticated visitor
        // straight to bare /login with no return path, which would drop the request id + one-time
        // token this page needs. This route handles "not logged in" itself (caching the full
        // path+query via cacheLoginRedirectPath before redirecting) so the admin lands back here
        // after signing in.
        path: ROUTE_PATHS.COLLECTION_REQUEST_REVIEW,
        lazy: {
          Component: importWithRetry(() => import('@dmp/pages/CollectionRequestReview/CollectionRequestReview')),
        },
      },
      {
        Component: AuthLayout,
        children: [
          {
            path: ROUTE_PATHS.LOGIN,
            lazy: {
              Component: importWithRetry(() => import('@dmp/pages/Auth/Login/Login')),
            },
          },
          {
            path: ROUTE_PATHS.REGISTER,
            lazy: {
              Component: importWithRetry(() => import('@dmp/pages/Auth/Register/Register')),
            },
          },
          {
            path: ROUTE_PATHS.FORGET_PASSWORD,
            lazy: {
              Component: importWithRetry(() => import('@dmp/pages/Auth/ForgetPassword/ForgetPassword')),
            },
          },
          {
            path: ROUTE_PATHS.SSO_LOGIN,
            lazy: {
              Component: importWithRetry(() => import('@dmp/pages/Auth/SsoLogin/SsoLogin')),
            },
          },
        ],
      },
      {
        Component: PolicyLayout,
        children: [
          {
            path: ROUTE_PATHS.PRIVACY_POLICY,
            lazy: {
              Component: importWithRetry(() => import('@dmp/pages/Policy/PrivacyPolicy/PrivacyPolicy')),
            },
          },
          {
            path: ROUTE_PATHS.COOKIE_POLICY,
            lazy: {
              Component: importWithRetry(() => import('@dmp/pages/Policy/CookiePolicy/CookiePolicy')),
            },
          },
          {
            path: ROUTE_PATHS.TERMS_OF_USE,
            lazy: {
              Component: importWithRetry(() => import('@dmp/pages/Policy/TermsOfUse/TermsOfUse')),
            },
          },
          {
            path: ROUTE_PATHS.USER_AGREEMENT,
            lazy: {
              Component: importWithRetry(() => import('@dmp/pages/Policy/UserAgreement/UserAgreement')),
            },
          },
          {
            path: ROUTE_PATHS.FAQ,
            lazy: {
              Component: importWithRetry(() => import('@dmp/pages/Policy/FAQ/FAQ')),
            },
          },
        ],
      },
      {
        path: '*',
        lazy: {
          Component: importWithRetry(() => import('@dmp/pages/NotFound/NotFound')),
        },
      },
    ],
  },
]);
